import { useEffect, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { OrganizationSwitcher, SignedIn, SignedOut, SignIn, RedirectToSignIn, useAuth, useOrganizationList } from '@clerk/clerk-react'
import Layout from './components/shared/Layout'
import { SessionStatus } from './components/shared/SessionStatus'
import RequireAdmin from './components/shared/RequireAdmin'
import { safeInternalRedirect } from './lib/safeRedirect'

// Existing pages
import LocationWizard from './pages/LocationWizard'
import CompetitorDiscovery from './pages/CompetitorDiscovery'
import PriceIntelligence from './pages/PriceIntelligence'
import PriceDrops from './pages/PriceDrops'
import AlertFeed from './pages/AlertFeed'
import BillingUsage from './pages/BillingUsage'
import NotificationSettings from './pages/NotificationSettings'
import LocationManagement from './pages/LocationManagement'
import CancellationFlow from './pages/CancellationFlow'

// New pages
import CompetitorProfile from './pages/CompetitorProfile'
import PriceHistory from './pages/PriceHistory'
import CatalogComparison from './pages/CatalogComparison'
import BrandCarrier from './pages/BrandCarrier'
import RivalDensity from './pages/RivalDensity'
import AlertDetail from './pages/AlertDetail'
import PriceChangeDeepDive from './pages/PriceChangeDeepDive'
import PromoDeepDive from './pages/PromoDeepDive'
import CompetitorRanking from './pages/CompetitorRanking'
import MyBenchmarks from './pages/MyBenchmarks'
import SkuGapAnalysis from './pages/SkuGapAnalysis'
import DealEffectiveness from './pages/DealEffectiveness'
import TeamAnnotations from './pages/TeamAnnotations'
import LocationAccess from './pages/LocationAccess'
import TeamManagement from './pages/TeamManagement'
import DataTrust from './pages/DataTrust'
import Offboarded from './pages/Offboarded'
import ActionQueue from './pages/ActionQueue'
import ReconciliationReview from './pages/ReconciliationReview'
import MenuProblems from './pages/MenuProblems'
import Analyst from './pages/Analyst'
import Rivals from './pages/Rivals'
import SupportCenter from './pages/SupportCenter'
import AccessPending from './pages/AccessPending'

/**
 * Clerk permits a signed-in session to have no active organization even when
 * the user belongs to one. API tokens from that state have no org_id claim,
 * which would otherwise route requests to a blank personal tenant. Activate a
 * sole membership before rendering any data-fetching child. With multiple
 * memberships, require an explicit choice rather than guessing across tenants.
 */
function SessionOrganizationGate({ children }: { children: React.ReactNode }) {
  const { isLoaded: authLoaded, isSignedIn, orgId } = useAuth()
  const { isLoaded: orgsLoaded, setActive, userMemberships } = useOrganizationList({ userMemberships: true })
  const activationStarted = useRef(false)
  const [activationError, setActivationError] = useState(false)

  useEffect(() => {
    if (!authLoaded || !orgsLoaded || !isSignedIn || orgId || userMemberships.isLoading) return
    if (userMemberships.count !== 1 || activationStarted.current) return
    const onlyOrganization = userMemberships.data[0]?.organization.id
    if (!onlyOrganization) return
    activationStarted.current = true
    void setActive({ organization: onlyOrganization }).catch(() => {
      activationStarted.current = false
      setActivationError(true)
    })
  }, [authLoaded, isSignedIn, orgId, orgsLoaded, setActive, userMemberships.count, userMemberships.data, userMemberships.isLoading])

  if (!authLoaded || !orgsLoaded || (isSignedIn && userMemberships.isLoading)) {
    return <SessionStatus />
  }
  if (isSignedIn && !orgId && userMemberships.count === 1 && !activationError) {
    return <SessionStatus>Opening your CannaSpy workspace…</SessionStatus>
  }
  if (isSignedIn && !orgId && userMemberships.count > 1) {
    return (
      <SessionStatus>
        <span style={{ marginRight: 14 }}>Choose the workspace you want to open:</span>
        <OrganizationSwitcher hidePersonal />
      </SessionStatus>
    )
  }
  if (activationError) {
    return <SessionStatus>We couldn’t open your CannaSpy workspace. Refresh the page to try again.</SessionStatus>
  }
  return <>{children}</>
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isLoaded } = useAuth()
  if (!isLoaded) return <SessionStatus />
  return (
    <>
      <SignedIn><SessionOrganizationGate>{children}</SessionOrganizationGate></SignedIn>
      <SignedOut><RedirectToSignIn /></SignedOut>
    </>
  )
}

/**
 * The real Clerk sign-in surface. Invitation-only: there is no public
 * self-service signup.
 *
 * Suppressing the sign-up affordance takes two supported settings together:
 *   • withSignUp={false} keeps sign-in and sign-up as separate flows (no inline
 *     sign-in-or-up), and
 *   • appearance hides the footer "Don't have an account? Sign up" action.
 *     The verified rendered descriptor on the sign-in card is
 *     elements.footerAction__signIn (Clerk scopes the footer action with the
 *     __signIn flow modifier — __signUp matches nothing here).
 * withSignUp alone does NOT remove that footer link; Clerk still renders it and
 * it is keyboard/pointer reachable, so the appearance rule is what makes the
 * sign-up link non-visible. No Clerk Dashboard change is involved.
 *
 * routing="path" + path="/sign-in" keeps Clerk on the in-app path (the parent
 * route is the catch-all /sign-in/* so Clerk's own sub-steps resolve without
 * falling back to hash routing).
 *
 * An already-signed-in visitor who opens /sign-in is sent on: to the Clerk
 * redirect_url when a protected deep link round-tripped through here (so the
 * deep-link destination is preserved), otherwise to /today. The destination is
 * resolved by safeInternalRedirect, which constrains it to same-origin app
 * paths and never back to /sign-in — protocol-relative ("//evil"), cross-origin,
 * and non-HTTP values all fall back to /today, so this can never loop or become
 * an open redirect.
 */
function SignInRoute() {
  const { isLoaded, isSignedIn } = useAuth()
  const [params] = useSearchParams()

  if (!isLoaded) return <SessionStatus />

  if (isSignedIn) {
    const dest = safeInternalRedirect(params.get('redirect_url'), window.location.origin)
    return <Navigate to={dest} replace />
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
      }}
    >
      <SignIn
        routing="path"
        path="/sign-in"
        fallbackRedirectUrl="/today"
        withSignUp={false}
        appearance={{ elements: { footerAction__signIn: { display: 'none' } } }}
      />
    </div>
  )
}

// /locations/:locationId → Today, scoped to that store.
// `replace` so the retired URL does not sit in the back-button history.
function LocationRedirect() {
  const { locationId } = useParams()
  return <Navigate to={locationId ? `/today?location=${locationId}` : '/today'} replace />
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Auth surface — invitation-only, no public self-service signup.
            /sign-in/* is a catch-all so Clerk's path-routed sub-steps resolve.
            /sign-up redirects to /sign-in (invitation-only, not a waitlist). */}
        <Route path="/sign-in/*" element={<SignInRoute />} />
        <Route path="/sign-up/*" element={<Navigate to="/sign-in" replace />} />
        <Route
          path="/offboarded"
          element={
            <>
              <SignedIn><Offboarded /></SignedIn>
              <SignedOut><RedirectToSignIn /></SignedOut>
            </>
          }
        />

        {/* Access-pending — a signed-in identity that is not yet provisioned in
            CannaSpy. Rendered OUTSIDE the data-fetching Layout: it makes zero
            protected API calls and shows only the invitation-only denial copy.
            Requires a Clerk session; a signed-out visitor round-trips to
            sign-in. */}
        <Route
          path="/access-pending"
          element={
            <>
              <SignedIn><AccessPending /></SignedIn>
              <SignedOut><RedirectToSignIn /></SignedOut>
            </>
          }
        />

        {/* Setup flow — the fake org-setup form is retired; /setup and
            /setup/org land on /today. The remaining steps stay protected. */}
        <Route path="/setup" element={<Navigate to="/today" replace />} />
        <Route path="/setup/org" element={<Navigate to="/today" replace />} />
        <Route path="/setup/locations" element={<ProtectedRoute><LocationWizard /></ProtectedRoute>} />
        <Route path="/setup/competitors" element={<ProtectedRoute><CompetitorDiscovery /></ProtectedRoute>} />

        {/* Protected app routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/today" replace />} />
          <Route path="today" element={<ActionQueue />} />
          <Route path="command-center" element={<Navigate to="/today" replace />} />
          <Route path="analyst" element={<Analyst />} />

          {/* Locations */}
          <Route path="locations" element={<LocationManagement />} />
          {/* Screen 05 (Location Dashboard) is retired. It had grown into a
              weaker copy of the Command Center — same map, same rail, same
              alerts with the product name and price delta stripped out — while
              the three things that would have made it distinct (price position,
              new-since-last-visit, rival promotions) were never built. Command
              Center now carries the location in the URL, so every existing link
              to a per-location page lands on the same store, on the screen that
              does the job properly. */}
          <Route path="locations/:locationId" element={<LocationRedirect />} />
          <Route path="settings/location-access" element={<LocationAccess />} />
          <Route path="settings/team" element={<TeamManagement />} />

          {/* Price intelligence */}
          <Route path="prices" element={<PriceIntelligence />} />
          <Route path="prices/history" element={<PriceHistory />} />
          <Route path="prices/catalog" element={<CatalogComparison />} />
          <Route path="prices/brands" element={<BrandCarrier />} />
          <Route path="prices/change/:eventId" element={<PriceChangeDeepDive />} />

          {/* Price Drops (formerly Promotions Tracker — banner-level Promotions
              view retired). Canonical route is /price-drops; /promotions
              redirects so existing links/bookmarks don't break. */}
          <Route path="price-drops" element={<PriceDrops />} />
          <Route path="promotions" element={<Navigate to="/price-drops" replace />} />
          <Route path="promotions/:promoId" element={<PromoDeepDive />} />

          {/* Alerts */}
          <Route path="alerts" element={<AlertFeed />} />
          <Route path="alerts/:alertId" element={<AlertDetail />} />

          {/* Rivals */}
          <Route path="rivals" element={<Rivals />} />

          {/* Competitors */}
          <Route path="competitors/:rivalId" element={<CompetitorProfile />} />

          {/* Retired feature: old bookmarks resolve to current rival tracking. */}
          <Route path="blocks/*" element={<Navigate to="/rivals" replace />} />

          {/* Market intelligence */}
          {/* Heat Map removed — redirect any stale link to the ranking view */}
          <Route path="market/heat-map" element={<Navigate to="/market/ranking" replace />} />
          <Route path="market/ranking" element={<CompetitorRanking />} />
          <Route path="market/rival-density" element={<RivalDensity />} />
          <Route path="market/benchmarks" element={<MyBenchmarks />} />
          <Route path="market/sku-gaps" element={<SkuGapAnalysis />} />
          <Route path="market/deals" element={<DealEffectiveness />} />

          {/* Team */}
          <Route path="team/annotations" element={<TeamAnnotations />} />
          <Route path="actions" element={<Navigate to="/today" replace />} />

          {/* Admin — frontend guard; backend requireAdmin is the real security boundary */}
          <Route path="admin/reconciliation" element={<RequireAdmin><ReconciliationReview /></RequireAdmin>} />
          <Route path="admin/menu-problems" element={<RequireAdmin><MenuProblems /></RequireAdmin>} />

          {/* Billing & settings */}
          <Route path="billing" element={<BillingUsage />} />
          <Route path="billing/upcoming" element={<Navigate to="/billing" replace />} />
          <Route path="settings/notifications" element={<NotificationSettings />} />
          <Route path="data-trust" element={<DataTrust />} />
          <Route path="data-health" element={<Navigate to="/data-trust" replace />} />
          <Route path="support" element={<SupportCenter />} />
          <Route path="cancel" element={<CancellationFlow />} />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}
